"use client";
import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface FoodSafetySiteNameContextType {
  siteNamesBySiteId: Record<string, string>;
  updateSiteName: (siteId: string, siteName: string) => void;
  getSiteName: (siteId: string) => string | undefined;
  clearSiteName: (siteId: string) => void;
}

const FoodSafetySiteNameContext = createContext<FoodSafetySiteNameContextType | undefined>(undefined);

export const FoodSafetySiteNameProvider = ({ children }: { children: ReactNode }) => {
  const [siteNamesBySiteId, setSiteNamesBySiteId] = useState<Record<string, string>>({});

  const updateSiteName = useCallback((siteId: string, siteName: string) => {
    if (!siteId) return;
    setSiteNamesBySiteId(prev => ({
      ...prev,
      [siteId]: siteName
    }));
  }, []);

  const getSiteName = useCallback((siteId: string): string | undefined => {
    if (!siteId) return undefined;
    return siteNamesBySiteId[siteId];
  }, [siteNamesBySiteId]);

  const clearSiteName = useCallback((siteId: string) => {
    if (!siteId) return;
    setSiteNamesBySiteId(prev => {
      const updated = { ...prev };
      delete updated[siteId];
      return updated;
    });
  }, []);

  const contextValue: FoodSafetySiteNameContextType = useMemo(() => ({
    siteNamesBySiteId,
    updateSiteName,
    getSiteName,
    clearSiteName
  }), [siteNamesBySiteId, updateSiteName, getSiteName, clearSiteName]);

  return (
    <FoodSafetySiteNameContext.Provider value={contextValue}>
      {children}
    </FoodSafetySiteNameContext.Provider>
  );
};

export const useFoodSafetySiteNameContext = () => {
  const context = useContext(FoodSafetySiteNameContext);
  if (context === undefined) {
    throw new Error('useFoodSafetySiteNameContext must be used within a FoodSafetySiteNameProvider');
  }
  return context;
};
